import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { callClaude } from '../utils/claudeProxy';
import {
  collection, addDoc, getDoc, getDocs, doc, updateDoc, deleteDoc,
  query, where, serverTimestamp
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, IconButton, Button,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Chip, CircularProgress, Divider, Alert, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress, Collapse, Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import * as mammoth from 'mammoth';
import { authFetch } from '../utils/authFetch';

// ── Kriterier medarbetare ──────────────────────────────────────────
const CRITERIA = [
  {
    key: 'kompetens',
    label: 'Kompetens',
    desc: 'Kunskap, färdighet, erfarenhet, beslutsförmåga och förmågan att göra rätt.',
  },
  {
    key: 'kreativitet',
    label: 'Kreativitet',
    desc: 'Förmågan att finna nya lösningar.',
  },
  {
    key: 'socialKompetens',
    label: 'Social kompetens',
    desc: 'Viljan och förmågan att hjälpa arbetskamrater, visa god kamratskap, relationer med ledare och arbetskamrater',
  },
  {
    key: 'hangivenhet',
    label: 'Hängivenhet',
    desc: 'Förmågan till engagemang för arbetsuppgifter och företaget, hög moral och vilja till hårt arbete i svåra situationer',
  },
  {
    key: 'enkelt',
    label: 'Enkelt',
    desc: 'Förmågan att arbeta efter enkelhet',
  },
  {
    key: 'affarsmassighet',
    label: 'Affärsmässighet',
    desc: 'Förmågan att genomföra arbetsuppgifterna kostnadseffektivt till ökad kundnytta och kundnöjdhet',
  },
];

const RATING_OPTIONS = [
  'Enligt förväntan',
  'Över förväntan',
  'Långt över förväntan',
];

// Frågor för Gruppledare-IUP (från PDF v4.0)
const GL_QUESTIONS = [
  { key: 'q1', label: '1. Min nuvarande roll på Tikab vill jag beskriva så här (arbetsuppgifter, utmaning, arbetsbörda och trivsel):' },
  { key: 'q2', label: '2. Min önskade roll på Tikab vill jag beskriva så här (arbetsuppgifter, utmaning, arbetsbörda och trivsel):' },
  { key: 'q3', label: '3. Jag upplever min ledande roll på Tikab på följande sätt:' },
  { key: 'q4', label: '4. Saker jag lärt mig/utvecklat under året:' },
  { key: 'q5', label: '5. För att fortsätta utvecklas (både som medarbetare och ledare) behöver jag bibehålla (egenskaper och kompetenser):' },
  { key: 'q6', label: '6. För att fortsätta utvecklas (både som medarbetare och ledare) behöver jag förstärka och utveckla (egenskaper och kompetenser):' },
];
const GL_Q7_LABEL = '7. Något som jag hamnat i senaste veckan som jag upplevt som stimulande och positivt:';
const GL_Q8_LABEL = '8. Något som jag hamnat i senaste veckan som jag upplevt som ostimulerande och negativt:';
const GL_Q9_LABEL = '9. Det här skulle jag vilja att Tikab gör mer av:';

// Frågor för AO-ansvarigs IUP (strategisk nivå)
const AO_QUESTIONS = [
  { key: 'q1', label: '1. Min nuvarande roll som Affärsområdesansvarig vill jag beskriva så här (affärsområdets status, resultat, utmaningar och trivsel):' },
  { key: 'q2', label: '2. Min önskade utveckling av affärsområdet vill jag beskriva så här (strategiska mål, förändringar, tillväxt):' },
  { key: 'q3', label: '3. Jag upplever min strategiska och operativa ledarroll på Tikab på följande sätt:' },
  { key: 'q4', label: '4. Saker jag lärt mig/utvecklat som ledare och affärsområdesansvarig under året:' },
  { key: 'q5', label: '5. För att fortsätta utvecklas som affärsområdesansvarig behöver jag bibehålla (egenskaper och kompetenser):' },
  { key: 'q6', label: '6. För att fortsätta utvecklas behöver jag förstärka och utveckla (egenskaper och kompetenser):' },
];
const AO_Q7_LABEL = '7. Något som gått särskilt bra under perioden (affärsmässigt, ledarskap, kundrelationer):';
const AO_Q8_LABEL = '8. Något som jag upplevt som utmanande eller negativt under perioden:';
const AO_Q9_LABEL = '9. Det här skulle jag vilja att Tikab gör mer av på organisationsnivå:';

// ── AI-import av IUP-dokument ──────────────────────────────────────

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function extractTextFromFile(file) {
  if (file.type === 'application/pdf') {
    const base64 = await fileToBase64(file);
    const json = await callClaude({
      model: 'claude-haiku-4-5-20251001',
      max_tokens: 4000,
      messages: [{
        role: 'user',
        content: [
          { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: base64 } },
          { type: 'text', text: 'Extrahera all text från detta dokument och returnera det som ren text.' }
        ]
      }]
    });
    const text = json.content?.[0]?.text || '';
    if (!text) throw new Error('Kunde inte extrahera text från PDF:en. Prova en annan fil.');
    return text;
  } else if (file.name.endsWith('.docx')) {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  }
  throw new Error('Filformat stöds ej. Använd PDF eller Word (.docx).');
}

async function analyzeIupText(text, iupType) {
  const ratingValues = '"Enligt förväntan", "Över förväntan" eller "Långt över förväntan" – eller tom sträng om ej angivet';

  const promptByType = {
    medarbetare: `Du analyserar ett IUP-dokument (Individuell Utvecklingsplan) för en medarbetare.
Extrahera innehållet och returnera ENBART giltig JSON, inget annat:
{
  "kompetens":       { "date": "YYYY-MM-DD eller tom sträng", "rating": ${ratingValues}, "motivation": "text eller tom sträng" },
  "kreativitet":     { "date": "", "rating": "", "motivation": "" },
  "socialKompetens": { "date": "", "rating": "", "motivation": "" },
  "hangivenhet":     { "date": "", "rating": "", "motivation": "" },
  "enkelt":          { "date": "", "rating": "", "motivation": "" },
  "affarsmassighet": { "date": "", "rating": "", "motivation": "" },
  "individualGoal":     "text eller tom sträng",
  "otherComments":      "text eller tom sträng",
  "mutualCommitments":  "text eller tom sträng"
}`,
    gruppledare: `Du analyserar ett IUP-dokument (Individuell Utvecklingsplan) för en gruppledare.
Extrahera svaren på frågorna och returnera ENBART giltig JSON, inget annat:
{
  "q1": "svar på fråga 1 – nuvarande roll",
  "q2": "svar på fråga 2 – önskad roll",
  "q3": "svar på fråga 3 – ledarrollen",
  "q4": "svar på fråga 4 – lärdomar under året",
  "q5": "svar på fråga 5 – bevara egenskaper",
  "q6": "svar på fråga 6 – förstärka egenskaper",
  "q7": ["positivt exempel 1", "positivt exempel 2", "positivt exempel 3"],
  "q8": ["negativt exempel 1", "negativt exempel 2", "negativt exempel 3"],
  "q9": "svar på fråga 9 – Tikab bör göra mer av"
}`,
    ao_ansvarig: `Du analyserar ett IUP-dokument (Individuell Utvecklingsplan) för en affärsområdesansvarig.
Extrahera svaren på frågorna och returnera ENBART giltig JSON, inget annat:
{
  "q1": "svar på fråga 1 – nuvarande roll/affärsområdets status",
  "q2": "svar på fråga 2 – önskad utveckling av affärsområdet",
  "q3": "svar på fråga 3 – strategisk/operativ ledarroll",
  "q4": "svar på fråga 4 – lärdomar som ledare",
  "q5": "svar på fråga 5 – bevara egenskaper",
  "q6": "svar på fråga 6 – förstärka egenskaper",
  "q7": ["positivt exempel 1", "positivt exempel 2", "positivt exempel 3"],
  "q8": ["utmaning/negativt 1", "utmaning/negativt 2", "utmaning/negativt 3"],
  "q9": "svar på fråga 9 – Tikab bör göra mer av"
}`
  };

  const prompt = (promptByType[iupType] || promptByType.medarbetare) + `\n\nDokumenttext:\n${text.slice(0, 6000)}`;

  const json = await callClaude({
    model: 'claude-haiku-4-5-20251001',
    max_tokens: 2000,
    messages: [{ role: 'user', content: prompt }]
  });
  const raw = json.content?.[0]?.text || '';
  if (!raw) throw new Error('AI returnerade ett tomt svar. Försök igen.');
  try { return JSON.parse(raw); } catch {
    // Strip markdown code fences if present
    const fenceMatch = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
    const bracketMatch = raw.match(/\{[\s\S]*\}/);
    const candidate = fenceMatch?.[1] ?? bracketMatch?.[0];
    if (candidate) {
      try { return JSON.parse(candidate); } catch {}
    }
    throw new Error('Kunde inte tolka AI-svaret. Prova med ett annat dokument.');
  }
}

// ── Formattera IUP-innehåll för AI-analys ─────────────────────────
function formatIupForAnalysis(iupType, formData) {
  if (iupType === 'gruppledare' || iupType === 'ao_ansvarig') {
    const questions = iupType === 'gruppledare' ? GL_QUESTIONS : AO_QUESTIONS;
    return questions.map(q => `${q.label}\n${formData[q.key] || '(Ej besvarat)'}`).join('\n\n') +
      `\n\nExempel positivt: ${(formData.q7 || []).filter(Boolean).join(', ') || '(Ej angivet)'}` +
      `\nExempel negativt: ${(formData.q8 || []).filter(Boolean).join(', ') || '(Ej angivet)'}` +
      `\nTikab bör göra mer av: ${formData.q9 || '(Ej angivet)'}`;
  }
  const criteriaText = CRITERIA.map(c => {
    const v = formData[c.key] || {};
    return `${c.label}: ${v.rating || '(ej angiven)'}${v.motivation ? ' — ' + v.motivation : ''}`;
  }).join('\n');
  const goalsText = (formData.goals || []).length > 0
    ? (formData.goals).map((g, i) => `Mål ${i+1}: ${g.mal || ''}${g.utfall ? ' (Utfall: ' + g.utfall + ')' : ''}`).join('\n')
    : (formData.individualGoal || '(Ej angivet)');
  return `Kriterier:\n${criteriaText}\n\nIndividuell målsättning:\n${goalsText}\n\nÖmsesidiga åtaganden: ${formData.mutualCommitments || '(Ej angivet)'}`;
}

// ── AI-vägledningsanalys (för chef) ───────────────────────────────
async function fetchDirectionContext(iupUserId, iupDeptId) {
  const [genSnap, indSnap] = await Promise.all([
    getDocs(query(collection(db, 'iupDirections'), where('scope', '==', 'general'))),
    getDocs(query(collection(db, 'iupDirections'), where('targetUserId', '==', iupUserId))),
  ]);
  const genDirs = genSnap.docs.map(d => d.data());
  const vdDir = genDirs.find(d => d.authorRole === 'vd' || d.authorRole === 'superadmin');
  const aoDir = genDirs.find(d => d.authorRole === 'ao_ansvarig' && d.departmentId === iupDeptId);
  const indDir = indSnap.docs.map(d => d.data()).find(d => d.scope === 'individual');
  return { vdDir, aoDir, indDir };
}

async function runDirectionAnalysis(iup, formData) {
  const { vdDir, aoDir, indDir } = await fetchDirectionContext(iup.userId, iup.departmentId);
  const iupContent = formatIupForAnalysis(iup.type, formData);

  const sections = [];
  if (vdDir?.content) sections.push(`**Bolagets generella riktning (VD):**\n${vdDir.content}`);
  if (aoDir?.content) sections.push(`**Affärsområdets riktning:**\n${aoDir.content}`);
  if (indDir?.content) sections.push(`**Chefens önskade förflyttning för denna person:**\n${indDir.content}`);
  sections.push(`**Personens IUP-svar:**\n${iupContent}`);

  const prompt = `Du är HR-konsult som stödjer chefer i IUP-samtal på Tikab, ett konsultbolag inom bygg- och fastighetssektorn. Baserat på nedanstående information, ge en strukturerad vägledning för samtalet (max 300 ord).

${sections.join('\n\n')}

Ge:
1. En kort sammanfattning av personens nuläge och ambitioner
2. En bedömning av hur väl de stämmer med önskad riktning
3. 2–3 konkreta rekommendationer för samtalet`;

  const json = await callClaude({
    model: 'claude-haiku-4-5-20251001',
    max_tokens: 1000,
    messages: [{ role: 'user', content: prompt }]
  });
  return json.content?.[0]?.text || '';
}

function IUPImportButton({ iupType, onImport, disabled }) {
  const [step, setStep] = useState('idle'); // idle | extracting | analyzing | confirm | error
  const [errorMsg, setErrorMsg] = useState('');
  const [preview, setPreview] = useState(null);

  async function handleFile(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setStep('extracting');
    setErrorMsg('');
    try {
      const text = await extractTextFromFile(file);
      setStep('analyzing');
      const result = await analyzeIupText(text, iupType);
      setPreview(result);
      setStep('confirm');
    } catch (err) {
      setErrorMsg(err.message || 'Något gick fel.');
      setStep('error');
    }
  }

  function handleConfirm() {
    onImport(preview);
    setStep('idle');
    setPreview(null);
  }

  function handleCancel() {
    setStep('idle');
    setPreview(null);
  }

  const busy = step === 'extracting' || step === 'analyzing';

  return (
    <>
      <Button
        component="label"
        variant="outlined"
        size="small"
        startIcon={busy ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
        disabled={disabled || busy}
        sx={{ borderColor: '#FF6B00', color: '#FF6B00',
          '&:hover': { borderColor: '#e65c00', bgcolor: '#fff3e0' } }}
      >
        {step === 'extracting' ? 'Läser dokument…'
          : step === 'analyzing' ? 'AI analyserar…'
          : 'Importera från dokument'}
        {!busy && <input type="file" accept=".pdf,.docx" hidden onChange={handleFile} />}
      </Button>

      {step === 'extracting' || step === 'analyzing' ? (
        <Box sx={{ mt: 1 }}>
          <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />
          <Typography variant="caption" color="text.secondary">
            {step === 'extracting' ? 'Extraherar text från dokument…' : 'AI tolkar innehållet…'}
          </Typography>
        </Box>
      ) : null}

      {step === 'error' && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setStep('idle')}>
          {errorMsg}
        </Alert>
      )}

      {/* Bekräftelsedialog */}
      <Dialog open={step === 'confirm'} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon sx={{ color: '#FF6B00' }} />
          AI hittade följande i dokumentet
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Granska vad som hittades. Vill du fylla i formuläret med detta innehåll?
            Befintliga svar skrivs över.
          </Typography>
          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 2,
            maxHeight: 320, overflowY: 'auto', fontSize: '0.78rem',
            fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(preview, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Avbryt</Button>
          <Button variant="contained" onClick={handleConfirm}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65c00' } }}>
            Fyll i formuläret
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Block-kommentarer ─────────────────────────────────────────────
function BlockCommentSection({ iupId, blockKey, allBlockComments, isManager, isOwner,
  currentUser, userName, iupOwnerId }) {
  const comments = allBlockComments?.[blockKey] || [];
  const [open, setOpen] = useState(false);
  const [newText, setNewText] = useState('');
  const [saving, setSaving] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  async function updateBlockComments(updated) {
    await updateDoc(doc(db, 'iups', iupId), {
      [`blockComments.${blockKey}`]: updated,
      updatedAt: serverTimestamp(),
    });
  }

  async function handleAddComment() {
    if (!newText.trim()) return;
    setSaving(true);
    try {
      const comment = {
        id: Date.now().toString(), authorId: currentUser.uid,
        authorName: userName, isManager: !!isManager,
        text: newText.trim(), createdAt: new Date().toISOString(),
        reaction: null, replies: [],
      };
      await updateBlockComments([...comments, comment]);
      if (isManager && iupOwnerId && iupOwnerId !== currentUser.uid) {
        await addDoc(collection(db, 'notifications'), {
          toUserId: iupOwnerId, fromUserId: currentUser.uid,
          fromUserName: userName, type: 'block_comment',
          iupId, blockKey, read: false, createdAt: serverTimestamp(),
        });
      }
      setNewText('');
    } finally { setSaving(false); }
  }

  async function handleReaction(commentId, reaction) {
    await updateBlockComments(comments.map(c =>
      c.id === commentId ? { ...c, reaction: c.reaction === reaction ? null : reaction } : c
    ));
  }

  async function handleReply(commentId) {
    if (!replyText.trim()) return;
    const reply = {
      id: Date.now().toString(), authorId: currentUser.uid,
      authorName: userName, text: replyText.trim(),
      createdAt: new Date().toISOString(),
    };
    const updated = comments.map(c =>
      c.id === commentId ? { ...c, replies: [...(c.replies || []), reply] } : c
    );
    await updateBlockComments(updated);
    const target = comments.find(c => c.id === commentId);
    if (target && target.authorId !== currentUser.uid) {
      await addDoc(collection(db, 'notifications'), {
        toUserId: target.authorId, fromUserId: currentUser.uid,
        fromUserName: userName, type: 'block_comment_reply',
        iupId, blockKey, read: false, createdAt: serverTimestamp(),
      });
    }
    setReplyText(''); setReplyTo(null);
  }

  const count = comments.length;
  return (
    <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Button size="small" startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />}
        onClick={() => setOpen(v => !v)}
        sx={{ fontSize: '0.72rem', p: 0.5, textTransform: 'none',
          color: count > 0 ? '#FF6B00' : 'text.secondary',
          '&:hover': { bgcolor: 'transparent' } }}>
        {count > 0 ? `${count} kommentar${count !== 1 ? 'er' : ''}` : 'Kommentera'}
      </Button>
      <Collapse in={open}>
        <Box sx={{ mt: 1, pl: 1.5, borderLeft: '2px solid #e0e0e0' }}>
          {comments.map(c => (
            <Box key={c.id} sx={{ mb: 1.5, display: 'flex', gap: 1 }}>
              <Avatar sx={{ width: 22, height: 22, flexShrink: 0, mt: 0.25,
                bgcolor: c.isManager ? '#FF6B00' : '#1a1a1a', fontSize: 10 }}>
                {(c.authorName || '?')[0].toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                  <Typography variant="caption" fontWeight="bold">{c.authorName}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {new Date(c.createdAt).toLocaleDateString('sv')}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontSize: '0.82rem', mt: 0.25 }}>{c.text}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
                  {isOwner && c.isManager && (
                    <>
                      <IconButton size="small" onClick={() => handleReaction(c.id, 'up')}
                        sx={{ p: 0.25, color: c.reaction === 'up' ? '#2e7d32' : 'action.disabled' }}>
                        <ThumbUpIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleReaction(c.id, 'down')}
                        sx={{ p: 0.25, color: c.reaction === 'down' ? '#d32f2f' : 'action.disabled' }}>
                        <ThumbDownIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </>
                  )}
                  {(isOwner || isManager) && (
                    <Button size="small" variant="text"
                      onClick={() => setReplyTo(replyTo === c.id ? null : c.id)}
                      sx={{ fontSize: '0.65rem', p: 0.25, minWidth: 0,
                        color: 'text.secondary', textTransform: 'none' }}>
                      Svara
                    </Button>
                  )}
                </Box>
                {(c.replies || []).map(r => (
                  <Box key={r.id} sx={{ mt: 0.75, pl: 1.5, borderLeft: '2px solid #f0f0f0',
                    display: 'flex', gap: 0.75 }}>
                    <Avatar sx={{ width: 18, height: 18, bgcolor: '#9e9e9e', fontSize: 9,
                      flexShrink: 0, mt: 0.25 }}>
                      {(r.authorName || '?')[0].toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.68rem' }}>
                        {r.authorName}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{r.text}</Typography>
                    </Box>
                  </Box>
                ))}
                {replyTo === c.id && (
                  <Box sx={{ mt: 0.75, display: 'flex', gap: 1 }}>
                    <TextField size="small" value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder="Skriv ett svar..."
                      sx={{ flex: 1, '& input': { fontSize: '0.8rem' } }} />
                    <Button size="small" variant="contained" onClick={() => handleReply(c.id)}
                      disabled={!replyText.trim()}
                      sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' }, fontSize: '0.7rem' }}>
                      Skicka
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
          {isManager && (
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <TextField size="small" fullWidth value={newText}
                onChange={e => setNewText(e.target.value)}
                placeholder="Kommentera detta block..."
                sx={{ '& input': { fontSize: '0.8rem' } }} />
              <Button size="small" variant="contained" onClick={handleAddComment}
                disabled={!newText.trim() || saving}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65c00' },
                  fontSize: '0.7rem', flexShrink: 0 }}>
                {saving ? '…' : 'Lägg till'}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Statuschip ─────────────────────────────────────────────────────
function StatusChip({ status }) {
  const map = {
    draft: { label: 'Utkast', color: 'default' },
    submitted: { label: 'Inlämnad', sx: { bgcolor: '#FF6B00', color: '#fff' } },
    reviewed: { label: 'Genomförd', color: 'success' },
  };
  const cfg = map[status] || map.draft;
  return (
    <Chip
      size="small"
      label={cfg.label}
      color={cfg.color}
      sx={cfg.sx}
    />
  );
}

// ── Medarbetare-formulär ──────────────────────────────────────────
function MedarbetareForm({ data, onChange, readOnly, isManager, template,
  blockComments, iupId, isOwner, currentUser, userName, iupOwnerId }) {
  const criteria = template?.criteria || CRITERIA;
  const ratingOptions = template?.ratingOptions || RATING_OPTIONS;
  const bcProps = { iupId, allBlockComments: blockComments, isManager, isOwner,
    currentUser, userName, iupOwnerId };

  function handleCriterion(key, field, value) {
    onChange({ ...data, [key]: { ...(data[key] || {}), [field]: value } });
  }

  function addGoal() {
    onChange({ ...data, goals: [...(data.goals || []), { mal: '', utfall: '' }] });
  }
  function removeGoal(idx) {
    onChange({ ...data, goals: (data.goals || []).filter((_, i) => i !== idx) });
  }
  function setGoal(idx, field, value) {
    onChange({ ...data, goals: (data.goals || []).map((g, i) => i === idx ? { ...g, [field]: value } : g) });
  }

  return (
    <Box>
      {criteria.map(c => (
        <Paper key={c.key} sx={{ p: 3, mb: 2, borderLeft: '4px solid #FF6B00' }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>{c.label}</Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2, color: 'text.secondary' }}>
            {c.desc}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Datum" type="date" InputLabelProps={{ shrink: true }}
                value={data[c.key]?.date || ''}
                onChange={e => handleCriterion(c.key, 'date', e.target.value)}
                disabled={readOnly} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <FormControl fullWidth>
                <InputLabel>Bedömning</InputLabel>
                <Select value={data[c.key]?.rating || ''} label="Bedömning"
                  onChange={e => handleCriterion(c.key, 'rating', e.target.value)}
                  disabled={readOnly}>
                  {ratingOptions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={5} label="Motivering"
                value={data[c.key]?.motivation || ''}
                onChange={e => handleCriterion(c.key, 'motivation', e.target.value)}
                disabled={readOnly} />
            </Grid>
          </Grid>
          {iupId && <BlockCommentSection blockKey={c.key} {...bcProps} />}
        </Paper>
      ))}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">Individuell Målsättning</Typography>
          {!readOnly && (
            <Button size="small" startIcon={<AddIcon />} onClick={addGoal}>
              Lägg till mål
            </Button>
          )}
        </Box>
        {(data.goals || []).map((goal, idx) => (
          <Box key={idx} sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold">Mål {idx + 1}</Typography>
              {!readOnly && (
                <IconButton size="small" onClick={() => removeGoal(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <TextField fullWidth multiline minRows={2} label="Mål"
              value={goal.mal || ''}
              onChange={e => setGoal(idx, 'mal', e.target.value)}
              disabled={readOnly} sx={{ mb: 1 }} />
            <TextField fullWidth multiline minRows={2} label="Utfall"
              value={goal.utfall || ''}
              onChange={e => setGoal(idx, 'utfall', e.target.value)}
              disabled={readOnly} />
          </Box>
        ))}
        {(data.goals || []).length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Inga mål tillagda.{!readOnly && ' Klicka "Lägg till mål" för att börja.'}
          </Typography>
        )}
        {iupId && <BlockCommentSection blockKey="individualGoal" {...bcProps} />}
      </Paper>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>Övriga kommentarer</Typography>
        <TextField fullWidth multiline minRows={5} value={data.otherComments || ''}
          onChange={e => onChange({ ...data, otherComments: e.target.value })} disabled={readOnly} />
        {iupId && <BlockCommentSection blockKey="otherComments" {...bcProps} />}
      </Paper>

      <Paper sx={{ p: 3, mb: 2, border: isManager ? '2px solid #FF6B00' : undefined }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>Ömsesidiga åtaganden</Typography>
        {isManager && (
          <Typography variant="caption" sx={{ color: '#FF6B00', display: 'block', mb: 1 }}>
            Du kan redigera detta fält som chef
          </Typography>
        )}
        <TextField fullWidth multiline minRows={5} value={data.mutualCommitments || ''}
          onChange={e => onChange({ ...data, mutualCommitments: e.target.value })}
          disabled={readOnly && !isManager} />
        {iupId && <BlockCommentSection blockKey="mutualCommitments" {...bcProps} />}
      </Paper>
    </Box>
  );
}

// ── Gemensamt ledarformulär (Gruppledare + AO-ansvarig) ───────────
function LedarIUPForm({ data, onChange, readOnly, questions, q7Label, q8Label, q9Label,
  blockComments, iupId, isManager, isOwner, currentUser, userName, iupOwnerId }) {
  const bcProps = { iupId, allBlockComments: blockComments, isManager, isOwner,
    currentUser, userName, iupOwnerId };
  function setField(key, value) {
    onChange({ ...data, [key]: value });
  }

  function setQ7(idx, value) {
    const arr = [...(data.q7 || ['', '', ''])];
    arr[idx] = value;
    setField('q7', arr);
  }

  function setQ8(idx, value) {
    const arr = [...(data.q8 || ['', '', ''])];
    arr[idx] = value;
    setField('q8', arr);
  }

  function addGoal() {
    const goals = [...(data.goals || []), { mal: '', utfall: '' }];
    setField('goals', goals);
  }

  function removeGoal(idx) {
    const goals = (data.goals || []).filter((_, i) => i !== idx);
    setField('goals', goals);
  }

  function setGoal(idx, field, value) {
    const goals = (data.goals || []).map((g, i) =>
      i === idx ? { ...g, [field]: value } : g
    );
    setField('goals', goals);
  }

  function setActivity(key, value) {
    setField('activities', { ...(data.activities || {}), [key]: value });
  }

  return (
    <Box>
      {questions.map(q => (
        <Paper key={q.key} sx={{ p: 3, mb: 2, borderLeft: '4px solid #FF6B00' }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>{q.label}</Typography>
          <TextField fullWidth multiline minRows={3}
            value={data[q.key] || ''}
            onChange={e => setField(q.key, e.target.value)}
            disabled={readOnly} />
          {iupId && <BlockCommentSection blockKey={q.key} {...bcProps} />}
        </Paper>
      ))}

      {/* Q7 */}
      <Paper sx={{ p: 3, mb: 2, borderLeft: '4px solid #FF6B00' }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>{q7Label}</Typography>
        {[0, 1, 2].map(i => (
          <TextField key={i} fullWidth multiline minRows={2} label={`${i + 1}.`}
            value={(data.q7 || ['', '', ''])[i] || ''}
            onChange={e => setQ7(i, e.target.value)} disabled={readOnly} sx={{ mb: 1.5 }} />
        ))}
        {iupId && <BlockCommentSection blockKey="q7" {...bcProps} />}
      </Paper>

      {/* Q8 */}
      <Paper sx={{ p: 3, mb: 2, borderLeft: '4px solid #FF6B00' }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>{q8Label}</Typography>
        {[0, 1, 2].map(i => (
          <TextField key={i} fullWidth multiline minRows={2} label={`${i + 1}.`}
            value={(data.q8 || ['', '', ''])[i] || ''}
            onChange={e => setQ8(i, e.target.value)} disabled={readOnly} sx={{ mb: 1.5 }} />
        ))}
        {iupId && <BlockCommentSection blockKey="q8" {...bcProps} />}
      </Paper>

      {/* Q9 */}
      <Paper sx={{ p: 3, mb: 2, borderLeft: '4px solid #FF6B00' }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>{q9Label}</Typography>
        <TextField fullWidth multiline minRows={3} value={data.q9 || ''}
          onChange={e => setField('q9', e.target.value)} disabled={readOnly} />
        {iupId && <BlockCommentSection blockKey="q9" {...bcProps} />}
      </Paper>

      {/* Mål */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">Mål och utfall</Typography>
          {!readOnly && (
            <Button size="small" startIcon={<AddIcon />} onClick={addGoal}>
              Lägg till mål
            </Button>
          )}
        </Box>
        {(data.goals || []).map((goal, idx) => (
          <Box key={idx} sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" fontWeight="bold">Mål {idx + 1}</Typography>
              {!readOnly && (
                <IconButton size="small" onClick={() => removeGoal(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Mål"
              value={goal.mal || ''}
              onChange={e => setGoal(idx, 'mal', e.target.value)}
              disabled={readOnly}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Utfall"
              value={goal.utfall || ''}
              onChange={e => setGoal(idx, 'utfall', e.target.value)}
              disabled={readOnly}
            />
          </Box>
        ))}
        {(data.goals || []).length === 0 && (
          <Typography variant="body2" color="text.secondary">Inga mål tillagda.</Typography>
        )}
      </Paper>

      {/* Aktiviteter */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Aktiviteter för utveckling
        </Typography>
        {[
          { key: 'forslag', label: 'Förslag' },
          { key: 'utbildning', label: 'Utbildning' },
          { key: 'utveckling', label: 'Utveckling' },
          { key: 'aktivitet', label: 'Aktivitet' },
        ].map(a => (
          <TextField
            key={a.key}
            fullWidth
            multiline
            minRows={2}
            label={a.label}
            value={data.activities?.[a.key] || ''}
            onChange={e => setActivity(a.key, e.target.value)}
            disabled={readOnly}
            sx={{ mb: 2 }}
          />
        ))}
      </Paper>
    </Box>
  );
}

// ── Tunna wrappers som skickar rätt frågor till LedarIUPForm ──────
function GruppledarForm({ data, onChange, readOnly, template, ...bcProps }) {
  const questions = template?.questions || GL_QUESTIONS;
  const q7Label = template?.q7Label || GL_Q7_LABEL;
  const q8Label = template?.q8Label || GL_Q8_LABEL;
  const q9Label = template?.q9Label || GL_Q9_LABEL;
  return (
    <LedarIUPForm data={data} onChange={onChange} readOnly={readOnly}
      questions={questions} q7Label={q7Label} q8Label={q8Label} q9Label={q9Label}
      {...bcProps} />
  );
}

function AoAnsvarigForm({ data, onChange, readOnly, template, ...bcProps }) {
  const questions = template?.questions || AO_QUESTIONS;
  const q7Label = template?.q7Label || AO_Q7_LABEL;
  const q8Label = template?.q8Label || AO_Q8_LABEL;
  const q9Label = template?.q9Label || AO_Q9_LABEL;
  return (
    <LedarIUPForm data={data} onChange={onChange} readOnly={readOnly}
      questions={questions} q7Label={q7Label} q8Label={q8Label} q9Label={q9Label}
      {...bcProps} />
  );
}

// ── Generell kommentarsektion (synlig för både chef och ägare) ────
function CommentsSection({ comments, onAddComment }) {
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSend() {
    if (!newComment.trim()) return;
    setSaving(true);
    await onAddComment(newComment.trim());
    setNewComment('');
    setSaving(false);
  }

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
        Kommentarer
      </Typography>
      {(comments || []).length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Inga kommentarer ännu.
        </Typography>
      )}
      {(comments || []).map(c => (
        <Box key={c.id} sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" fontWeight="bold">{c.authorName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {c.createdAt ? new Date(c.createdAt).toLocaleString('sv-SE') : ''}
            </Typography>
          </Box>
          <Typography variant="body2">{c.text}</Typography>
        </Box>
      ))}
      <Divider sx={{ my: 2 }} />
      <TextField
        fullWidth
        multiline
        minRows={2}
        label="Skriv en kommentar..."
        value={newComment}
        onChange={e => setNewComment(e.target.value)}
        sx={{ mb: 1 }}
      />
      <Button
        variant="contained"
        startIcon={<SendIcon />}
        disabled={!newComment.trim() || saving}
        onClick={handleSend}
        sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}
      >
        {saving ? 'Skickar...' : 'Skicka'}
      </Button>
    </Paper>
  );
}

// ── Hjälpfunktioner ───────────────────────────────────────────────
function extractFormData(iup) {
  if (!iup) return {};
  if (iup.type === 'medarbetare') {
    return {
      kompetens: iup.kompetens || {},
      kreativitet: iup.kreativitet || {},
      socialKompetens: iup.socialKompetens || {},
      hangivenhet: iup.hangivenhet || {},
      enkelt: iup.enkelt || {},
      affarsmassighet: iup.affarsmassighet || {},
      goals: iup.goals || (iup.individualGoal ? [{ mal: iup.individualGoal, utfall: '' }] : []),
      otherComments: iup.otherComments || '',
      mutualCommitments: iup.mutualCommitments || '',
    };
  }
  // gruppledare + ao_ansvarig delar samma fältstruktur
  return {
    q1: iup.q1 || '', q2: iup.q2 || '', q3: iup.q3 || '',
    q4: iup.q4 || '', q5: iup.q5 || '', q6: iup.q6 || '',
    q7: iup.q7 || ['', '', ''],
    q8: iup.q8 || ['', '', ''],
    q9: iup.q9 || '',
    goals: iup.goals || [],
    activities: iup.activities || {},
  };
}

function emptyMedarbetare() {
  const data = {};
  CRITERIA.forEach(c => { data[c.key] = { date: '', rating: '', motivation: '' }; });
  data.goals = [];
  data.otherComments = '';
  data.mutualCommitments = '';
  return data;
}

function emptyLedar() {
  return {
    q1: '', q2: '', q3: '', q4: '', q5: '', q6: '',
    q7: ['', '', ''],
    q8: ['', '', ''],
    q9: '',
    goals: [],
    activities: { forslag: '', utbildning: '', utveckling: '', aktivitet: '' },
  };
}

async function summarizeIupAI(iup) {
  const isLedar = iup.type !== 'medarbetare';
  let content = '';
  if (!isLedar) {
    const CRIT_KEYS = ['kompetens','kreativitet','socialKompetens','hangivenhet','enkelt','affarsmassighet'];
    content = CRIT_KEYS.map(k => {
      const v = iup[k] || {};
      return `${k}: ${v.rating || ''} — ${v.motivation || ''}`;
    }).join('\n') +
    `\nMål: ${(iup.goals || []).map((g,i) => `${i+1}. ${g.mal}`).join(' | ') || iup.individualGoal || ''}` +
    `\nÖmsesidiga åtaganden: ${iup.mutualCommitments || ''}`;
  } else {
    content = ['q1','q2','q3','q4','q5','q6'].map(k => `${k}: ${iup[k] || ''}`).join('\n') +
      `\nq7: ${(iup.q7 || []).join(' | ')}` +
      `\nq8: ${(iup.q8 || []).join(' | ')}` +
      `\nq9: ${iup.q9 || ''}`;
  }

  const prompt = !isLedar
    ? `Du sammanfattar en medarbetares IUP-rapport kortfattat för att ge en bra startpunkt inför nästa samtal. Returnera ENBART giltig JSON med dessa fält (kortare text, max 2 meningar per fält):
{"kompetens":{"date":"","rating":"","motivation":""},"kreativitet":{"date":"","rating":"","motivation":""},"socialKompetens":{"date":"","rating":"","motivation":""},"hangivenhet":{"date":"","rating":"","motivation":""},"enkelt":{"date":"","rating":"","motivation":""},"affarsmassighet":{"date":"","rating":"","motivation":""},"goals":[{"mal":"","utfall":""}],"otherComments":"","mutualCommitments":""}

IUP-innehåll:\n${content}`
    : `Du sammanfattar en ledares IUP-rapport kortfattat för att ge en bra startpunkt inför nästa samtal. Returnera ENBART giltig JSON med dessa fält (kortare text, max 2 meningar per fält):
{"q1":"","q2":"","q3":"","q4":"","q5":"","q6":"","q7":["","",""],"q8":["","",""],"q9":""}

IUP-innehåll:\n${content}`;

  const json = await callClaude({
    model: 'claude-haiku-4-5-20251001',
    max_tokens: 2000,
    messages: [{ role: 'user', content: prompt }]
  });
  const raw = json.content?.[0]?.text || '';
  const match = raw.match(/```(?:json)?\s*([\s\S]*?)```/) || [null, raw];
  return JSON.parse(match[1].trim());
}

async function createNotification(db, { toUserId, fromUserId, fromUserName, iupId }) {
  if (!toUserId) return;
  await addDoc(collection(db, 'notifications'), {
    toUserId,
    fromUserId,
    fromUserName,
    type: 'iup_submitted',
    iupId,
    read: false,
    createdAt: new Date().toISOString(),
  });
}

// ── Listvy – mina IUPs ────────────────────────────────────────────
function MyIUPList({ currentUser, userProfile }) {
  const navigate = useNavigate();
  const [iups, setIups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  const loadIups = useCallback(async () => {
    setLoading(true);
    try {
      // Undvik composite index-krav — hämta utan orderBy, sortera client-side
      const snap = await getDocs(query(
        collection(db, 'iups'),
        where('userId', '==', currentUser.uid)
      ));
      const all = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const at = a.createdAt?.toDate?.()?.getTime() ?? 0;
          const bt = b.createdAt?.toDate?.()?.getTime() ?? 0;
          return bt - at;
        });
      setIups(all);
    } catch (e) {
      console.error('loadIups:', e);
    } finally {
      setLoading(false);
    }
  }, [currentUser.uid]);

  useEffect(() => { loadIups(); }, [loadIups]);

  function handleCreateNew() {
    setCreateDialog(true);
    setCreateError('');
  }

  async function handleCreateMode(mode) {
    const latest = iups[0] || null;
    const role = userProfile?.role;
    const iupType = (role === 'ao_ansvarig' || userProfile?.isDepartmentManager) ? 'ao_ansvarig'
      : role === 'gruppledare' ? 'gruppledare'
      : 'medarbetare';
    const today = new Date().toISOString().slice(0, 10);

    setCreateLoading(true);
    setCreateError('');
    try {
      let formData;
      if (mode === 'empty') {
        formData = iupType === 'medarbetare' ? emptyMedarbetare() : emptyLedar();
      } else if (mode === 'copy' && latest) {
        formData = extractFormData(latest);
      } else if (mode === 'ai' && latest) {
        formData = await summarizeIupAI(latest);
      } else {
        formData = iupType === 'medarbetare' ? emptyMedarbetare() : emptyLedar();
      }

      const ref = await addDoc(collection(db, 'iups'), {
        userId: currentUser.uid,
        userName: userProfile?.name || currentUser.displayName || currentUser.email,
        departmentId: userProfile?.departmentId || '',
        type: iupType,
        period: today,
        status: 'draft',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        submittedAt: null,
        comments: [],
        seenByManager: {},
        ...formData,
      });
      setCreateDialog(false);
      navigate('/hr/iup/' + ref.id);
    } catch (e) {
      setCreateError(e.message || 'Något gick fel.');
    }
    setCreateLoading(false);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    await deleteDoc(doc(db, 'iups', deleteTarget.id));
    setIups(prev => prev.filter(i => i.id !== deleteTarget.id));
    setDeleteTarget(null);
    setDeleting(false);
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2 }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">Mina IUP-samtal</Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            Individuell Utvecklingsplan
          </Typography>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65c00' } }}
          >
            Nytt samtal
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ width: '100%', mt: 2, mb: 4 }}>
            <Box sx={{ height: 3, bgcolor: '#FF6B00', borderRadius: 2,
              animation: 'pulse 1.2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%': { opacity: 1 }, '50%': { opacity: 0.3 }, '100%': { opacity: 1 }
              }
            }} />
          </Box>
        ) : iups.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Inga IUP-samtal ännu. Klicka på "Nytt samtal" för att starta.
            </Typography>
          </Paper>
        ) : (
          iups.map(iup => (
            <Paper key={iup.id} sx={{ p: 2.5, mb: 2, display: 'flex',
              alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography fontWeight="bold">
                  {iup.period || 'Utan datum'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {{ gruppledare: 'Gruppledare-IUP', ao_ansvarig: 'AO-ansvarig-IUP', medarbetare: 'Medarbetare-IUP' }[iup.type] || 'IUP'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <StatusChip status={iup.status} />
                <Button size="small" variant="outlined"
                  onClick={() => navigate('/hr/iup/' + iup.id)}>
                  Öppna
                </Button>
                {iup.status !== 'reviewed' && (
                  <IconButton size="small" onClick={() => setDeleteTarget(iup)}
                    sx={{ color: 'error.light' }}>
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            </Paper>
          ))
        )}
      </Container>

      {/* Skapa nytt IUP – välj läge */}
      <Dialog open={createDialog} onClose={() => !createLoading && setCreateDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Skapa nytt IUP-samtal</DialogTitle>
        <DialogContent>
          {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Välj hur det nya IUP-arket ska fyllas i:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Button variant="outlined" fullWidth disabled={createLoading}
              onClick={() => handleCreateMode('empty')}
              sx={{ justifyContent: 'flex-start', py: 1.5, textAlign: 'left' }}>
              <Box>
                <Typography fontWeight="bold" variant="body2">Tomt ark</Typography>
                <Typography variant="caption" color="text.secondary">Börja från början utan förval</Typography>
              </Box>
            </Button>
            {iups.length > 0 && (
              <>
                <Button variant="outlined" fullWidth disabled={createLoading}
                  onClick={() => handleCreateMode('copy')}
                  sx={{ justifyContent: 'flex-start', py: 1.5, textAlign: 'left' }}>
                  <Box>
                    <Typography fontWeight="bold" variant="body2">Kopiera föregående</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Alla svar följer med som startpunkt
                    </Typography>
                  </Box>
                </Button>
                <Button variant="outlined" fullWidth disabled={createLoading}
                  onClick={() => handleCreateMode('ai')}
                  sx={{ justifyContent: 'flex-start', py: 1.5, textAlign: 'left',
                    borderColor: '#FF6B00', color: '#FF6B00',
                    '&:hover': { borderColor: '#e65c00', bgcolor: '#fff3e0' } }}>
                  <Box>
                    {createLoading
                      ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} sx={{ color: '#FF6B00' }} />
                          <Typography variant="body2">AI sammanfattar…</Typography>
                        </Box>
                      : <>
                          <Typography fontWeight="bold" variant="body2">AI-sammanfattning</Typography>
                          <Typography variant="caption" color="text.secondary">
                            AI kondenserar föregående IUP till kortare startpunkter
                          </Typography>
                        </>
                    }
                  </Box>
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)} disabled={createLoading}>Avbryt</Button>
        </DialogActions>
      </Dialog>

      {/* Radera-bekräftelse */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /> Radera IUP-samtal
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Radera samtal <strong>{deleteTarget?.period || 'Utan datum'}</strong>?
            Åtgärden kan inte ångras.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Avbryt</Button>
          <Button variant="contained" color="error" disabled={deleting}
            onClick={handleDelete}>
            {deleting ? 'Raderar...' : 'Radera'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── IUP-formulär ──────────────────────────────────────────────────
function IUPForm({ iupId, currentUser, userProfile, templates }) {
  const navigate = useNavigate();
  const [iup, setIup] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [sharedDir, setSharedDir] = useState(null);
  const [directionAnalysis, setDirectionAnalysis] = useState(null);
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [analysisError, setAnalysisError] = useState('');

  const isOwner = iup?.userId === currentUser.uid;

  // Determine if viewer is the manager of this IUP's owner
  const isManagerViewing = !isOwner && (
    userProfile?.role === 'gruppledare' ||
    userProfile?.role === 'ao_ansvarig' ||
    userProfile?.isDepartmentManager ||
    userProfile?.role === 'superadmin' ||
    userProfile?.role === 'vd'
  );

  // For medarbetare IUP: manager can edit mutualCommitments
  const canEditMutualCommitments = isManagerViewing && iup?.type === 'medarbetare';

  const formReadOnly = !isOwner || iup?.status === 'reviewed';

  useEffect(() => {
    async function loadIUP() {
      setLoading(true);
      const snap = await getDoc(doc(db, 'iups', iupId));
      if (!snap.exists()) {
        setError('IUP-dokumentet hittades inte.');
        setLoading(false);
        return;
      }
      const data = { id: snap.id, ...snap.data() };
      setIup(data);
      setFormData(extractFormData(data));
      if (data.directionAnalysis) setDirectionAnalysis(data.directionAnalysis);
      setLoading(false);

      // Fetch shared direction for owner
      if (data.userId === currentUser.uid) {
        try {
          // sharedWithTarget-filtret krävs för att reglerna ska tillåta frågan
          // för en medarbetare utan chefsroll
          const sharedSnap = await getDocs(query(
            collection(db, 'iupDirections'),
            where('targetUserId', '==', data.userId),
            where('sharedWithTarget', '==', true)
          ));
          const shared = sharedSnap.docs
            .map(d => d.data())
            .find(d => d.scope === 'individual' && d.sharedWithTarget === true);
          setSharedDir(shared || null);
        } catch (_) {}
      }

      // Mark as seen by manager
      if (!data.seenByManager?.[currentUser.uid] &&
          data.userId !== currentUser.uid) {
        try {
          await updateDoc(doc(db, 'iups', iupId), {
            [`seenByManager.${currentUser.uid}`]: true,
          });
        } catch (_) {}
      }
    }
    loadIUP();
  }, [iupId, currentUser]);

  async function handleSave() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'iups', iupId), {
        ...formData,
        period: iup?.period ?? null,
        updatedAt: serverTimestamp(),
      });
      setSavedMsg('Sparat!');
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e) {
      setError('Kunde inte spara: ' + e.message);
    }
    setSaving(false);
  }

  async function handleSubmit() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'iups', iupId), {
        ...formData,
        period: iup?.period ?? null,
        status: 'submitted',
        submittedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Find manager to notify
      let toUserId = null;
      if (iup.type === 'medarbetare') {
        // Notify the gruppledare
        const ownerSnap = await getDoc(doc(db, 'users', iup.userId));
        toUserId = ownerSnap.data()?.gruppledarId || null;
      } else if (iup.type === 'gruppledare') {
        // Notify AO-ansvarig in same dept
        // Notify AO-ansvarig: primärt AO, isDepartmentManager eller managedDepartmentIds
        const [q1snap, q2snap, q3snap] = await Promise.all([
          getDocs(query(collection(db, 'users'),
            where('departmentId', '==', iup.departmentId),
            where('role', '==', 'ao_ansvarig'))),
          getDocs(query(collection(db, 'users'),
            where('departmentId', '==', iup.departmentId),
            where('isDepartmentManager', '==', true))),
          getDocs(query(collection(db, 'users'),
            where('managedDepartmentIds', 'array-contains', iup.departmentId))),
        ]);
        const aoSnap = !q1snap.empty ? q1snap : !q2snap.empty ? q2snap : q3snap;
        if (!aoSnap.empty) toUserId = aoSnap.docs[0].id;
      } else if (iup.type === 'ao_ansvarig') {
        // Notify VD
        const q = query(collection(db, 'users'), where('role', '==', 'vd'));
        const snap = await getDocs(q);
        if (!snap.empty) toUserId = snap.docs[0].id;
      }

      const fromName = userProfile?.name || currentUser.displayName || currentUser.email;

      await createNotification(db, {
        toUserId,
        fromUserId: currentUser.uid,
        fromUserName: fromName,
        iupId,
      });

      // Send email notification to manager
      if (toUserId) {
        try {
          const managerSnap = await getDoc(doc(db, 'users', toUserId));
          const managerEmail = managerSnap.data()?.email || managerSnap.data()?.microsoftEmail;
          const managerName = managerSnap.data()?.name || '';
          if (managerEmail) {
            const iupUrl = `${window.location.origin}/hr/iup/${iupId}`;
            await authFetch('/.netlify/functions/iup-submitted', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                to: managerEmail,
                toName: managerName,
                fromName,
                iupType: iup.type,
                period: iup?.period || '',
                iupUrl,
              }),
            });
          }
        } catch (_) {
          // Email failure is non-fatal — notification already sent in-app
        }
      }

      setIup(prev => ({ ...prev, status: 'submitted' }));
      setSubmitDone(true);
      setSavedMsg('IUP inlämnad!');
      setTimeout(() => setSavedMsg(''), 3000);
    } catch (e) {
      setError('Kunde inte lämna in: ' + e.message);
    }
    setSaving(false);
  }

  async function handleMarkReviewed() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'iups', iupId), {
        status: 'reviewed',
        updatedAt: serverTimestamp(),
      });
      setIup(prev => ({ ...prev, status: 'reviewed' }));
      setSavedMsg('Markerad som genomförd!');
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e) {
      setError('Kunde inte uppdatera: ' + e.message);
    }
    setSaving(false);
  }

  async function handleAddComment(text) {
    const comment = {
      id: Date.now().toString(),
      authorId: currentUser.uid,
      authorName: userProfile?.name || currentUser.displayName || currentUser.email,
      text,
      createdAt: new Date().toISOString(),
    };
    const updatedComments = [...(iup.comments || []), comment];
    await updateDoc(doc(db, 'iups', iupId), { comments: updatedComments });
    setIup(prev => ({ ...prev, comments: updatedComments }));
  }

  async function handleRunAnalysis() {
    setAnalysisRunning(true);
    setAnalysisError('');
    try {
      const result = await runDirectionAnalysis(iup, formData);
      setDirectionAnalysis(result);
    } catch (e) {
      setAnalysisError(e.message || 'Analys misslyckades.');
    }
    setAnalysisRunning(false);
  }

  async function handleSaveAnalysis() {
    try {
      await updateDoc(doc(db, 'iups', iupId), {
        directionAnalysis, updatedAt: serverTimestamp(),
      });
      setSavedMsg('Analys sparad!');
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e) {
      setError('Kunde inte spara analys: ' + e.message);
    }
  }

  async function handleUnlock() {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'iups', iupId), {
        status: 'submitted', updatedAt: serverTimestamp(),
      });
      setIup(prev => ({ ...prev, status: 'submitted' }));
      setSavedMsg('IUP upplåst för redigering.');
      setTimeout(() => setSavedMsg(''), 2500);
    } catch (e) {
      setError('Kunde inte låsa upp: ' + e.message);
    }
    setSaving(false);
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (error && !iup) return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Alert severity="error">{error}</Alert>
    </Container>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <LagbasLogo />
        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
        <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            IUP – {iup?.userName || ''}
          </Typography>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            {{ gruppledare: 'Gruppledare-IUP', ao_ansvarig: 'AO-ansvarig-IUP', medarbetare: 'Medarbetare-IUP' }[iup?.type] || 'IUP'}
            {iup?.period ? ' · Samtalsdatum: ' + iup.period : ''}
          </Typography>
        </Box>
        <StatusChip status={iup?.status} />
      </Box>

      <Container maxWidth="md" sx={{ py: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {savedMsg && <Alert severity="success" sx={{ mb: 2 }}>{savedMsg}</Alert>}

        {isManagerViewing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Du läser {iup?.userName}s IUP som chef.
            {canEditMutualCommitments && ' Du kan redigera fältet "Ömsesidiga åtaganden".'}
          </Alert>
        )}

        {/* Samtalsdatum + AI-import (editable by owner) */}
        {isOwner && (
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start',
              gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Samtalsdatum"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={iup?.period || ''}
                onChange={e => setIup(prev => ({ ...prev, period: e.target.value }))}
                sx={{ width: 200 }}
              />
              <Box>
                <IUPImportButton
                  iupType={iup?.type}
                  disabled={iup?.status === 'submitted' || iup?.status === 'reviewed'}
                  onImport={imported => setFormData(prev => ({ ...prev, ...imported }))}
                />
              </Box>
            </Box>
          </Paper>
        )}

        {/* Form */}
        {(() => {
          const bcProps = {
            blockComments: iup?.blockComments || {},
            iupId,
            isManager: isManagerViewing,
            isOwner,
            currentUser,
            userName: userProfile?.name || currentUser.displayName || currentUser.email,
            iupOwnerId: iup?.userId,
          };
          return iup?.type === 'medarbetare' ? (
            <MedarbetareForm data={formData} onChange={setFormData}
              readOnly={formReadOnly} isManager={isManagerViewing}
              template={templates?.medarbetare} {...bcProps} />
          ) : iup?.type === 'ao_ansvarig' ? (
            <AoAnsvarigForm data={formData} onChange={setFormData}
              readOnly={formReadOnly} template={templates?.ao_ansvarig} {...bcProps} />
          ) : (
            <GruppledarForm data={formData} onChange={setFormData}
              readOnly={formReadOnly} template={templates?.gruppledare} {...bcProps} />
          );
        })()}

        {/* Action buttons */}
        <Paper sx={{ p: 2.5, mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap',
          alignItems: 'center' }}>
          {/* Owner can save and submit — locked when reviewed */}
          {isOwner && iup?.status !== 'reviewed' && (
            <>
              <Button variant="contained" disabled={saving} onClick={handleSave}
                sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
                {saving ? 'Sparar...' : 'Spara'}
              </Button>
              {iup?.status !== 'submitted' && (
                <Button variant="contained" startIcon={<SendIcon />}
                  disabled={saving || submitDone} onClick={handleSubmit}
                  sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e65c00' } }}>
                  Lämna in
                </Button>
              )}
            </>
          )}
          {isOwner && iup?.status === 'reviewed' && (
            <Chip label="Genomförd — låst" color="success" size="small" />
          )}

          {/* Manager can save mutualCommitments and mark reviewed */}
          {isManagerViewing && canEditMutualCommitments && (
            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSave}
              sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}
            >
              {saving ? 'Sparar...' : 'Spara åtaganden'}
            </Button>
          )}
          {isManagerViewing && iup?.status === 'submitted' && (
            <Button variant="contained" startIcon={<CheckCircleIcon />}
              disabled={saving} onClick={handleMarkReviewed}
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
              Markera som genomförd
            </Button>
          )}
          {isManagerViewing && iup?.status === 'reviewed' && (
            <Button variant="outlined" startIcon={<LockOpenIcon />}
              disabled={saving} onClick={handleUnlock}
              sx={{ borderColor: '#9e9e9e', color: '#9e9e9e',
                '&:hover': { borderColor: '#666', color: '#666' } }}>
              Lås upp för redigering
            </Button>
          )}
        </Paper>

        {/* Delad riktning – visas för ägaren om chefen delat */}
        {isOwner && sharedDir?.content && (
          <Alert
            icon={<TrendingUpIcon />}
            severity="info"
            sx={{ mt: 2 }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Din chef har delat en riktning med dig
            </Typography>
            <Typography variant="body2">{sharedDir.content}</Typography>
          </Alert>
        )}

        {/* Vägledningsanalys – bara för chef */}
        {isManagerViewing && (
          <Paper sx={{ p: 2.5, mt: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold"
              sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUpIcon sx={{ color: '#FF6B00', fontSize: 18 }} />
              Vägledningsanalys
            </Typography>
            <Button
              variant="outlined" size="small"
              startIcon={analysisRunning ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
              disabled={analysisRunning}
              onClick={handleRunAnalysis}
              sx={{ mb: 1.5, borderColor: '#FF6B00', color: '#FF6B00',
                '&:hover': { borderColor: '#e65c00', bgcolor: '#fff3e0' } }}
            >
              {analysisRunning ? 'Analyserar...' : 'Kör AI-analys'}
            </Button>
            {analysisError && (
              <Alert severity="error" sx={{ mb: 1.5 }}>{analysisError}</Alert>
            )}
            {directionAnalysis !== null && (
              <>
                <TextField
                  multiline minRows={5} fullWidth size="small"
                  value={directionAnalysis}
                  onChange={e => setDirectionAnalysis(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Button size="small" variant="contained" onClick={handleSaveAnalysis}
                  sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
                  Spara analys
                </Button>
              </>
            )}
          </Paper>
        )}

        {/* Kommentarer — synliga för både chef och ägare */}
        {(isManagerViewing || isOwner) && (
          <CommentsSection comments={iup?.comments} onAddComment={handleAddComment} />
        )}
      </Container>
    </Box>
  );
}

// ── Huvudkomponent ────────────────────────────────────────────────
export default function IUP() {
  const { iupId } = useParams();
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [templates, setTemplates] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'users', currentUser.uid)).then(snap => {
      if (snap.exists()) setUserProfile(snap.data());
      setProfileLoading(false);
    });
  }, [currentUser]);

  useEffect(() => {
    Promise.all([
      getDoc(doc(db, 'iupTemplates', 'medarbetare')),
      getDoc(doc(db, 'iupTemplates', 'gruppledare')),
      getDoc(doc(db, 'iupTemplates', 'ao_ansvarig')),
    ]).then(([med, gl, ao]) => {
      setTemplates({
        medarbetare: med.exists() ? med.data() : null,
        gruppledare: gl.exists() ? gl.data() : null,
        ao_ansvarig: ao.exists() ? ao.data() : null,
      });
    }).catch(() => {});
  }, []);

  if (profileLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (iupId) {
    return (
      <IUPForm
        iupId={iupId}
        currentUser={currentUser}
        userProfile={userProfile}
        templates={templates}
      />
    );
  }

  return (
    <MyIUPList
      currentUser={currentUser}
      userProfile={userProfile}
    />
  );
}
